import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

interface IAnimatedTooltipLink {
  href: string;
  label: string;
}

interface IAnimatedTooltipProps {
  children: React.ReactNode;
  content: string;
  trigger?: "hover" | "click";
  position?: IAnimatedTooltipPosition;
  link?: IAnimatedTooltipLink;
  maxWidth?: number;
}

export type IAnimatedTooltipPosition = "top" | "bottom" | "left" | "right";

const AnimatedTooltip = ({
  children,
  content,
  trigger = "hover",
  position = "top",
  link,
  maxWidth,
}: IAnimatedTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(
    (rect: DOMRect) => {
      switch (position) {
        case "top":
          return {
            top: rect.top - 8,
            left: rect.left + rect.width / 2,
          };
        case "bottom":
          return {
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
          };
        case "left":
          return {
            top: rect.top + rect.height / 2,
            left: rect.left - 8,
          };
        case "right":
          return {
            top: rect.top + rect.height / 2,
            left: rect.right + 8,
          };
        default:
          return {
            top: rect.top - 8,
            left: rect.left + rect.width / 2,
          };
      }
    },
    [position],
  );

  useEffect(() => {
    if (isVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newPosition = calculatePosition(rect);
      setTooltipPosition(newPosition);
    }
  }, [isVisible, position, calculatePosition]);

  useEffect(() => {
    if (trigger === "click" && isVisible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsVisible(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [trigger, isVisible]);

  const handleMouseEnter = () => {
    if (trigger === "hover") {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === "hover") {
      setIsVisible(false);
    }
  };

  const handleClick = () => {
    if (trigger === "click") {
      setIsVisible(!isVisible);
    }
  };

  return (
    <>
      <TooltipContainer
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: trigger === "click" ? "pointer" : "default" }}
      >
        {children}
      </TooltipContainer>
      {isVisible &&
        createPortal(
          <TooltipContent
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              ...(maxWidth ? { maxWidth, whiteSpace: "normal" } : {}),
            }}
            onMouseEnter={trigger === "hover" ? handleMouseEnter : undefined}
            onMouseLeave={trigger === "hover" ? handleMouseLeave : undefined}
            $allowPointerEvents={trigger === "click" || !!link}
            $position={position}
          >
            {content}
            {link && (
              <TooltipLink
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </TooltipLink>
            )}
          </TooltipContent>,
          document.body,
        )}
    </>
  );
};

const fadeInTop = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -100%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -100%) translateY(0);
  }
`;

const fadeInBottom = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 0%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0%) translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translate(-100%, -50%) translateX(4px);
  }
  to {
    opacity: 1;
    transform: translate(-100%, -50%) translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translate(0%, -50%) translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translate(0%, -50%) translateX(0);
  }
`;

const TooltipContainer = styled.div`
  display: inline-block;
`;

const TooltipContent = styled.div<{
  $allowPointerEvents?: boolean;
  $position: "top" | "bottom" | "left" | "right";
}>`
  position: fixed;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.navy500};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  color: ${({ theme }) => theme.colors.chalk};
  border-radius: 8px;
  font-family: Barlow;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 10000;
  pointer-events: ${({ $allowPointerEvents }) =>
    $allowPointerEvents ? "auto" : "none"};
  opacity: 0;
  animation: ${({ $position }) => {
      switch ($position) {
        case "top":
          return fadeInTop;
        case "bottom":
          return fadeInBottom;
        case "left":
          return fadeInLeft;
        case "right":
          return fadeInRight;
        default:
          return fadeInTop;
      }
    }}
    0.15s ease-out forwards;
`;

const TooltipLink = styled.a`
  color: ${({ theme }) => theme.colors.envy};
  display: block;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  margin-top: 8px;
  text-decoration: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

export { AnimatedTooltip };
